import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector, useAppStore } from "hooks/redux"
import { useDeleteTemplate, useSaveTemplate } from "resources/templates"
import {
  getRestoreViewIdOnMetricsSelectorClose,
  shouldClearDuplicateReturnViewId,
} from "connected-components/asset-view/helpers/duplicate-return-view"
import {
  graphSlice,
  DEFAULT_GRAPH_STATE,
  GRAPH_PREFERENCE_BASE,
  selectGraphPanel,
} from "connected-components/dashboard/store/graph-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { getToday } from "helpers/types/dates"
import { resolveDateOverride, getEffectiveLatestDate, type DateOverride } from "connected-components/dashboard/helpers/date-override"
import { OK, ERROR } from "helpers/data-statuses"
import { CONTENT_SET_TEMPLATE_PREFIX } from "../templates/template-chart-config"

// Pure slice reader — does NOT sync preferences. GraphWrapper calls useSyncGraphPreferences once;
// calling it here would make every consumer re-sync and crash outside a PreferenceContext (Storybook).
export function useGraphData(initialSelectedTemplate?: string, initialDateOverride?: DateOverride | null) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(GRAPH_PREFERENCE_BASE)
  const store = useAppStore()

  const graphInstance = useAppSelector((s) => selectGraphPanel(s, dashboardId, panelKey))
  const graphState = graphInstance ?? DEFAULT_GRAPH_STATE
  // Read the raw (un-coalesced) instance so GraphWrapper can hold the template tree until the slice
  // is seeded — otherwise nested consumers paint DEFAULT before the config seed lands.
  const isGraphInitialized = graphInstance != null

  // Layout effect for parity with the other panel seeds. Graph doesn't omit its field from the
  // fallback, so a saved template still overrides this seed; the phase only makes ordering deterministic.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for a template before latching, or a config arriving on a later render never seeds.
    if (initialSelectedTemplate == null) return
    seeded.current = true
    dispatch(graphSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId: panelKey,
      selectedTemplate: initialSelectedTemplate,
      dateOverride: initialDateOverride,
    }))
  }, [dashboardId, panelKey, dispatch, initialSelectedTemplate, initialDateOverride])

  const setSelectedTemplate = useCallback((template: string) => {
    // Read fresh, not the render closure -- this fires mid-flow and a stale value would mis-detect which hop this is.
    const currentPanel = store.getState().dashboard.graph[dashboardId]?.[panelKey] ?? DEFAULT_GRAPH_STATE
    // Re-selecting the already-current template is a no-op, or it would wipe addedMetrics for a "switch" that never happened.
    if (template === currentPanel.selectedTemplate) return
    const isEnteringDuplicateFlow = template === "custom"
    const isRestoringFromDuplicateFlow = currentPanel.selectedTemplate === "custom"
      && currentPanel.duplicateReturnViewId === template

    if (shouldClearDuplicateReturnViewId(template)) {
      // Switching away from "custom" abandons the whole duplicate-from-view flow, not just its return id.
      dispatch(graphSlice.actions.clearDuplicateState({ dashboardId, panelId: panelKey }))
      dispatch(graphSlice.actions.setShowMetricsSelector({ dashboardId, panelId: panelKey, showMetricsSelector: false }))
    }
    // A genuine switch clears addedMetrics, but the Duplicate/Save-as-view hop to "custom" and back isn't one.
    if (!isEnteringDuplicateFlow && !isRestoringFromDuplicateFlow) {
      dispatch(graphSlice.actions.resetAddedMetrics({ dashboardId, panelId: panelKey }))
    }
    dispatch(graphSlice.actions.setSelectedTemplate({ dashboardId, panelId: panelKey, template }))
  }, [dispatch, dashboardId, panelKey, store])

  const setShowMetricsSelector = useCallback((show: boolean, { restoreSourceView = true }: { restoreSourceView?: boolean } = {}) => {
    dispatch(graphSlice.actions.setShowMetricsSelector({ dashboardId, panelId: panelKey, showMetricsSelector: show }))
    // restoreSourceView: false is the save/delete close path — state stays until the caller's success/failure branch resolves it.
    if (!show && restoreSourceView) {
      const freshPanelStateAfterClear = store.getState().dashboard.graph[dashboardId]?.[panelKey] ?? DEFAULT_GRAPH_STATE
      const restoreTemplateId = getRestoreViewIdOnMetricsSelectorClose(
        freshPanelStateAfterClear.duplicateReturnViewId,
        freshPanelStateAfterClear.selectedTemplate,
      )
      if (restoreTemplateId) {
        setSelectedTemplate(restoreTemplateId)
      }
      dispatch(graphSlice.actions.clearDuplicateState({ dashboardId, panelId: panelKey }))
    }
  }, [dispatch, dashboardId, panelKey, setSelectedTemplate, store])

  const graphUpdaters = useMemo(() => ({
    setSelectedTemplate,
    setDisabledItems: (itemId: string, defaultDisabled?: boolean) =>
      dispatch(graphSlice.actions.setDisabledItems({ dashboardId, panelId: panelKey, itemId, defaultDisabled })),
    setOnlyItem: (itemId: string, allItemIds: string[]) =>
      dispatch(graphSlice.actions.setOnlyItem({ dashboardId, panelId: panelKey, itemId, allItemIds })),
    enableItems: (itemIds: string[]) =>
      dispatch(graphSlice.actions.enableItems({ dashboardId, panelId: panelKey, itemIds })),
    setDateOverride: (dateOverride: DateOverride) =>
      dispatch(graphSlice.actions.setDateOverride({ dashboardId, panelId: panelKey, dateOverride })),
    clearDateOverride: () =>
      dispatch(graphSlice.actions.clearDateOverride({ dashboardId, panelId: panelKey })),
    addMetric: (metricId: string) =>
      dispatch(graphSlice.actions.addMetric({ dashboardId, panelId: panelKey, metricId })),
    resetAddedMetrics: () =>
      dispatch(graphSlice.actions.resetAddedMetrics({ dashboardId, panelId: panelKey })),
    setSeriesColorSlots: (seriesColorSlots: Record<string, number>) =>
      dispatch(graphSlice.actions.setSeriesColorSlots({ dashboardId, panelId: panelKey, seriesColorSlots })),
    setShowMetricsSelector,
    setDuplicateFromViewName: (duplicateFromViewName: string | null) =>
      dispatch(graphSlice.actions.setDuplicateFromViewName({ dashboardId, panelId: panelKey, duplicateFromViewName })),
    setDuplicateFromTemplateId: (duplicateFromTemplateId: string | null) =>
      dispatch(graphSlice.actions.setDuplicateFromTemplateId({ dashboardId, panelId: panelKey, duplicateFromTemplateId })),
    setDuplicateFromViewMetrics: (duplicateFromViewMetrics: string[] | null) =>
      dispatch(graphSlice.actions.setDuplicateFromViewMetrics({ dashboardId, panelId: panelKey, duplicateFromViewMetrics })),
    setDuplicateReturnViewId: (duplicateReturnViewId: string | null) =>
      dispatch(graphSlice.actions.setDuplicateReturnViewId({ dashboardId, panelId: panelKey, duplicateReturnViewId })),
    clearDuplicateState: () =>
      dispatch(graphSlice.actions.clearDuplicateState({ dashboardId, panelId: panelKey })),
  }), [dispatch, dashboardId, panelKey, setSelectedTemplate, setShowMetricsSelector])

  // Panel-level override supersedes the dashboard's dates when present; templates read
  // startDate/endDate transparently, so effectiveDates is spread last to win over ...core's raw dates.
  // resolveDateOverride re-derives startDate/endDate from the override's period against the panel's
  // *current* earliestDate/latestDate on every read (so a stored "3M" never goes stale, and a shrunk
  // range from an instrument/model switch is picked up) and derives a separate performanceStartDate,
  // since Performance queries with that pair instead of startDate/endDate.
  const effectiveDates = useMemo(() => {
    const effectiveLatestDate = getEffectiveLatestDate(core.isIntraday, core.endDate, core.latestDate)
    const resolved = graphState.dateOverride
      ? resolveDateOverride(graphState.dateOverride, core.earliestDate, effectiveLatestDate)
      : null
    const endDate = resolved?.endDate ?? core.endDate
    return {
      startDate: resolved?.startDate ?? core.startDate,
      endDate,
      performanceStartDate: resolved?.performanceStartDate ?? core.performanceStartDate,
      performanceEndDate: resolved?.endDate ?? core.performanceEndDate,
      // A stored override can still resolve to null (boundaries not loaded yet, or a collapsed
      // fixed-length window) — callers gate "is an override actually in effect" on this, not on
      // the raw dateOverride, since dates fall back to core's in that case.
      isDateOverrideActive: resolved != null,
      // core.isIntraday only means "live" when the panel's own effective endDate (post override)
      // is today — a panel viewing a historical single day (e.g. a collapsed 1D/MTD/YTD override)
      // must not route through the live intraday path just because the dashboard itself is.
      isIntradayMode: core.isIntraday && endDate === getToday(),
    }
  }, [graphState.dateOverride, core.isIntraday, core.earliestDate, core.latestDate, core.startDate, core.endDate, core.performanceStartDate, core.performanceEndDate])

  const deleteTemplate = useDeleteTemplate()
  const saveTemplate = useSaveTemplate()

  // Reads FRESH from the store, not the render closure, so an awaited caller doesn't compare against a stale captured id.
  const getCurrentViewId = useCallback(() => {
    const current = store.getState().dashboard.graph[dashboardId]?.[panelKey] ?? DEFAULT_GRAPH_STATE
    return current.selectedTemplate
  }, [store, dashboardId, panelKey])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...graphState,
    ...effectiveDates,
    isGraphInitialized,
    ...graphUpdaters,
    deleteTemplate,
    saveTemplate,
    getCurrentViewId,
  }), [core, modelId, trade, graphState, effectiveDates, isGraphInitialized, graphUpdaters, deleteTemplate, saveTemplate, getCurrentViewId])
}

export function isContentSetTemplateId(selectedTemplate: string | null | undefined): boolean {
  return Boolean(selectedTemplate?.startsWith(CONTENT_SET_TEMPLATE_PREFIX))
}

// isKnownNamedTemplate is passed in rather than computed here to avoid a circular import (templates import useGraphData).
export function resolveIsBuiltInGraphTemplate(selectedTemplate: string, isKnownNamedTemplate: boolean): boolean {
  return isKnownNamedTemplate || isContentSetTemplateId(selectedTemplate)
}

export interface GraphTemplateResolutionInputs {
  selectedTemplate: string
  setSelectedTemplate: (template: string) => void
  duplicateFromTemplateId: string | null
  duplicateReturnViewId: string | null
  isBuiltInTemplate: boolean
  customTemplates: { id: string }[] | null | undefined
  customTemplatesStatus: string
  templateId: string | null
  templateMetadataStatus: string
}

export interface GraphTemplateResolution {
  isBuiltInTemplate: boolean
  isCustomTemplate: boolean
  isExistingCustomTemplate: boolean
  isOrphanedCustomSentinel: boolean
  isStaleCustomTemplateId: boolean
  isTemplateListLoadFailure: boolean
  isSelectedTemplateLoadFailure: boolean
}

// Kept as a separate hook so useGraphData's other consumers don't pay for the GraphWrapper-only fetches (useTemplates, useTemplateMetadata) this depends on.
export function useGraphTemplateResolution({
  selectedTemplate,
  setSelectedTemplate,
  duplicateFromTemplateId,
  duplicateReturnViewId,
  isBuiltInTemplate,
  customTemplates,
  customTemplatesStatus,
  templateId,
  templateMetadataStatus,
}: GraphTemplateResolutionInputs): GraphTemplateResolution {
  const isCustomTemplate = !isBuiltInTemplate
  const isExistingCustomTemplate = Boolean(customTemplates?.find((template) => template.id === selectedTemplate))
  const isOrphanedCustomSentinel = selectedTemplate === "custom" && !duplicateFromTemplateId && !duplicateReturnViewId
  const isCustomDuplicateFlowActive = selectedTemplate === "custom" && Boolean(duplicateFromTemplateId || duplicateReturnViewId)
  const isStaleCustomTemplateId = isCustomTemplate && selectedTemplate !== "custom"
    && customTemplatesStatus === OK && !isExistingCustomTemplate

  const isCustomTemplatesListTerminal = customTemplatesStatus === OK || customTemplatesStatus === ERROR

  useEffect(() => {
    if (isBuiltInTemplate || !isCustomTemplatesListTerminal) return
    if (selectedTemplate === "custom") {
      if (isOrphanedCustomSentinel) {
        setSelectedTemplate(DEFAULT_GRAPH_STATE.selectedTemplate)
      }
      return
    }
    if (isStaleCustomTemplateId) {
      setSelectedTemplate(DEFAULT_GRAPH_STATE.selectedTemplate)
    }
  }, [
    isBuiltInTemplate,
    isCustomTemplatesListTerminal,
    selectedTemplate,
    isOrphanedCustomSentinel,
    isStaleCustomTemplateId,
    setSelectedTemplate,
  ])

  const isTemplateListLoadFailure = isCustomTemplate && customTemplatesStatus === ERROR && !isCustomDuplicateFlowActive
  const isSelectedTemplateLoadFailure = templateId != null && templateMetadataStatus === ERROR
    && customTemplatesStatus === OK && isExistingCustomTemplate

  return {
    isBuiltInTemplate,
    isCustomTemplate,
    isExistingCustomTemplate,
    isOrphanedCustomSentinel,
    isStaleCustomTemplateId,
    isTemplateListLoadFailure,
    isSelectedTemplateLoadFailure,
  }
}
